import { Box, Flex, Text } from "rebass/styled-components";
import styled, { css } from "styled-components";
import { useRecoilValue } from "recoil";
import { useEffect } from "react";

import { Moment } from "../Moment";
import { TimePill } from "../styledComponents";
import { TMoment, TMatch } from "MVVM/Views/MatchHistory/@types";
import { MomentsViewModel } from "./ViewModel";
import { MomentsLoading } from "../../MatchesTableLoading";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { NoMoments } from "../NoMoments";
import {
  MatchHistoryGame,
  MatchHistoryWidgetSourceState,
} from "MVVM/State/matchHistory";
import { usePortal } from "MVVM/Hooks/usePortal";
import { MatchHistoryLoginModal } from "../../LoginModal";
import { useUser } from "MVVM/Hooks/useUser";
import { formatGameMode } from "MVVM/Views/MatchHistory/utils";

const MomentsList = ({
  match,
  isWidget,
  outdated,
  numMomentsToRender,
}: {
  match: TMatch;
  isWidget?: boolean;
  outdated: boolean;
  numMomentsToRender?: number;
}) => {
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const limit = isWidget ? 3 : 20;
  const widgetSource = useRecoilValue(MatchHistoryWidgetSourceState);
  const { allstarUser } = useUser();
  const momentIdQueryParam = new URLSearchParams(window.location.search).get(
    "momentId",
  );
  const { moments, loading, requestOnDemandMoments } = MomentsViewModel(
    match.id,
    limit,
  );
  const momentForModal = moments.find(
    (moment: TMoment) => moment.id === momentIdQueryParam,
  );

  const matchTitle =
    !!match.title && !momentForModal ? (
      <Flex
        flexDirection="column"
        style={{ alignItems: "center", justifyContent: "center" }}
      >
        {match.title}
        <Flex style={{ fontSize: "20px", gap: "6px" }}>
          <Text
            color={
              ["Win", "Victory"].includes(match.result)
                ? "envy"
                : match.result === "Tie"
                ? "error"
                : "alert"
            }
          >
            {match.result}
          </Text>
          &bull;
          <Box>{formatGameMode(match.gameMode)}</Box>
        </Flex>
      </Flex>
    ) : (
      <>{match.title}</>
    );

  const {
    portalOpen: matchHistoryLoginPortalOpen,
    setPortalOpen: setMatchHistoryLoginPortalOpen,
    Portal: MatchHistoryLoginPortal,
  } = usePortal(
    <MatchHistoryLoginModal
      game={matchHistoryGame}
      moment={momentForModal}
      matchTitle={matchTitle}
      title="matchHistoryLoginModal.title"
      defaultTitle="Don't Miss Out On This Play!"
      subtitle="matchHistoryLoginModal.subtitle"
      defaultSubtitle="Create a FREE Allstar.gg account to create, edit, and share a clip from this moment."
    />,
  );

  const momentsToRender = numMomentsToRender
    ? moments.slice(0, numMomentsToRender)
    : moments;

  const bestMomentId = moments.length
    ? moments.reduce(
        (best: TMoment, m: TMoment) => (m.score > best.score ? m : best),
        moments[0],
      ).id
    : null;

  useEffect(() => {
    if (!allstarUser.loggedIn && (momentForModal || match.title)) {
      if (!sessionStorage.getItem("mh_signup_modal_on_land_shown")) {
        sessionStorage.setItem("mh_signup_modal_on_land_shown", "true");
        setMatchHistoryLoginPortalOpen(true);
      }
      // else do nothing, modal already shown this session
    }
  }, [
    allstarUser.loggedIn,
    momentForModal,
    match.title,
    setMatchHistoryLoginPortalOpen,
  ]);

  return (
    <>
      {matchHistoryLoginPortalOpen && MatchHistoryLoginPortal}
      {loading && <MomentsLoading />}
      {!loading && !moments.length && (
        <NoMoments requestOnDemandMoments={requestOnDemandMoments} />
      )}
      {!loading &&
        !!moments.length &&
        momentsToRender.map((moment: TMoment, index: number) => (
          <MomentContainer
            key={moment.id}
            $widgetSource={widgetSource}
            numMomentsToRender={numMomentsToRender || 0}
          >
            <Timeline mr="4" className="timeline">
              {moment.id === bestMomentId ? (
                <BestTimePill>
                  <Text fontSize="0" style={{ minWidth: "auto" }}>
                    {moment.timelineLabel}
                  </Text>
                </BestTimePill>
              ) : (
                <TimePill>
                  <Text fontSize="0" style={{ minWidth: "auto" }}>
                    {moment.timelineLabel}
                  </Text>
                </TimePill>
              )}
            </Timeline>
            {moment.id === bestMomentId ? (
              <BestMomentWrapper>
                <BestMomentLabelWrapper>
                  <AnimatedTooltip content="Allstar determined this was this player's best moment from this match">
                    <BestMomentLabel>Player's Best Moment</BestMomentLabel>
                  </AnimatedTooltip>
                </BestMomentLabelWrapper>
                <Moment
                  moment={moment}
                  isWidget={isWidget}
                  outdated={outdated}
                  matchId={match.id}
                />
              </BestMomentWrapper>
            ) : (
              <Moment
                moment={moment}
                isWidget={isWidget}
                outdated={outdated}
                matchId={match.id}
              />
            )}
          </MomentContainer>
        ))}
    </>
  );
};

const MomentContainer = styled(Flex)<{
  $widgetSource: string;
  numMomentsToRender: number;
}>`
  :last-child :first-child.timeline {
    height: 0;
  }

  ${({ $widgetSource, numMomentsToRender }) =>
    $widgetSource === "iframe" &&
    !numMomentsToRender &&
    css`
      @media (max-height: 585px), (max-width: 650px) {
        :last-child {
          display: none;
        }
      }

      @media (max-height: 500px), (max-width: 500px) {
        :nth-last-child(2) {
          display: none;
        }
      }
    `}
`;

const Timeline = styled(Box)`
  background: linear-gradient(
    90deg,
    #0000 98%,
    ${({ theme }) => theme.colors.navy} 98%
  );
  background-size: 52%;
  min-width: 60px;

  width: 60px;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: none;
  }
`;

const BestTimePill = styled(TimePill)`
  background: rgba(255, 193, 47, 0.15);
  border-color: rgba(255, 193, 47, 0.4);
  color: #ffc12f;
`;

const BestMomentWrapper = styled(Box)`
  border: 1px solid rgba(255, 193, 47, 0.3);
  border-radius: 12px;
  flex-grow: 1;
  margin-bottom: 16px;
  overflow: hidden;
  position: relative;

  & > div {
    margin-bottom: 0;
  }
`;

const BestMomentLabelWrapper = styled(Box)`
  background: rgba(255, 193, 47, 0.1);
  width: 100%;
`;

const BestMomentLabel = styled(Text)`
  color: #ffc12f;
  cursor: default;
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 6px 12px;
  text-transform: uppercase;
`;

export { MomentsList };
